import { useQuery } from '@tanstack/react-query';
import { orpc } from '../../apis/orpc-query';
import { StatusText } from '../../components/ui/StatusText';
import { formatText } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { catalogueStatusTone } from './condition-display';

/** Read-only: tightening a protocol means publishing a new version, never rewriting history (plan §3.3). */
export const ProtocolsTab = () => {
  const { t } = useI18nTranslation();

  const protocols = useQuery(orpc.protocols.list.queryOptions({ input: { page: 1, pageSize: 100, sortBy: 'code', sortDirection: 'asc' } }));

  const headers = [
    t('conditions.protocols.columns.code'),
    t('conditions.protocols.columns.version'),
    t('conditions.protocols.columns.name'),
    t('conditions.protocols.columns.metric'),
    t('conditions.protocols.columns.unit'),
    t('conditions.protocols.columns.effective'),
    t('conditions.protocols.columns.status'),
  ];

  return (
    <div className="space-y-3">
      <p className="max-w-2xl text-sm text-muted-foreground">{t('conditions.protocols.subtitle')}</p>

      {protocols.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {t('errors.loadFailed')}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              {headers.map((header) => (
                <th
                  key={header}
                  scope="col"
                  className="whitespace-nowrap px-4 py-3 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(protocols.data?.items ?? []).length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length}
                  className="px-4 py-12 text-center text-muted-foreground"
                >
                  {protocols.isPending ? t('table.loading') : t('conditions.protocols.empty')}
                </td>
              </tr>
            ) : (
              (protocols.data?.items ?? []).map((protocol) => (
                <tr
                  key={protocol.id}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 text-start font-mono text-xs text-foreground">{protocol.code}</td>
                  <td className="px-4 py-3 text-start text-muted-foreground">v{protocol.version}</td>
                  <td className="px-4 py-3 text-start text-foreground">{protocol.name}</td>
                  <td className="px-4 py-3 text-start font-mono text-xs text-muted-foreground">{protocol.metric}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-start font-medium text-foreground">{protocol.unit}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-start text-muted-foreground">
                    {formatText([protocol.effectiveFrom, protocol.effectiveTo].filter(Boolean).join(' → '))}
                  </td>
                  <td className="px-4 py-3 text-start">
                    <StatusText
                      label={t(`enums.status.${protocol.status}`)}
                      tone={catalogueStatusTone(protocol.status)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
